import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Link } from 'react-router-dom';
import Confetti from 'react-confetti';
import { 
  RiFlowChart, RiImage2Line, RiGitBranchLine, RiRocket2Line, 
  RiRadarLine, RiShieldCheckLine, RiSearchEyeLine, RiMagicLine, 
  RiLineChartLine, RiShieldStarLine, RiBarChartBoxLine, RiBrushLine, 
  RiMegaphoneLine, RiLayoutGridLine, RiFocus2Line 
} from 'react-icons/ri';
import SEO from '../components/common/SEO';
import FAQSection from '../components/common/FAQSection';
import seoConfig from '../config/seo.json';
import { FaAws } from 'react-icons/fa';
import { SiGooglecloud } from 'react-icons/si';

// --- Utility Components ---
function ZoomableImage({ src, alt }) {
  const [zoom, setZoom] = React.useState(1);
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const MIN = 1; const MAX = 4;
  const clampZoom = (z) => Math.min(MAX, Math.max(MIN, z));

  React.useEffect(() => { setZoom(1); setOffset({ x: 0, y: 0 }); }, [src]);

  const handleWheel = React.useCallback((e) => {
    e.preventDefault();
    setZoom((z) => {
      const next = clampZoom(z - e.deltaY * 0.001 * z);
      if (next === MIN) setOffset({ x: 0, y: 0 });
      return next;
    });
  }, []);

  return (
    <div
      onWheel={handleWheel}
      style={{ position: 'relative', background: '#1e293b', overflow: 'hidden', height: 'calc(90vh - 130px)', cursor: zoom > 1 ? 'grab' : 'default' }}
    >
      <motion.div
        drag={zoom > 1}
        dragConstraints={{ left: -800, right: 800, top: -500, bottom: 500 }}
        dragElastic={0.04}
        animate={{ scale: zoom, x: offset.x, y: offset.y }}
        transition={{ type: 'spring', stiffness: 320, damping: 32 }}
        style={{ position: 'absolute', inset: 0, transformOrigin: 'center center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        onDragEnd={(_, info) => setOffset(o => ({ x: o.x + info.offset.x, y: o.y + info.offset.y }))}
      >
        <AnimatePresence mode="wait">
          <motion.img
            key={src}
            src={src}
            alt={alt}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            draggable={false}
            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', userSelect: 'none', pointerEvents: 'none', background: 'white', position: 'absolute', inset: 0 }}
          />
        </AnimatePresence>
      </motion.div>

      <div style={{ position: 'absolute', bottom: '14px', right: '14px', zIndex: 20, display: 'flex', gap: '6px', alignItems: 'center' }}>
        {[
          { label: '−', fn: () => setZoom(z => { const n = clampZoom(z - 0.5); if (n === MIN) setOffset({ x: 0, y: 0 }); return n; }) },
          { label: `${Math.round(zoom * 100)}%`, fn: () => { setZoom(1); setOffset({ x: 0, y: 0 }); } },
          { label: '+', fn: () => setZoom(z => clampZoom(z + 0.5)) },
        ].map(({ label, fn }) => (
          <button key={label} type="button" onClick={fn}
            style={{ minWidth: '36px', height: '34px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(15,23,42,0.88)', color: 'white', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', padding: '0 10px' }}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function useCountUp(target, duration = 700) {
  const [display, setDisplay] = React.useState(target);
  const prev = React.useRef(target);
  React.useEffect(() => {
    const start = prev.current;
    const diff = target - start;
    if (diff === 0) return;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(start + diff * e));
      if (p < 1) requestAnimationFrame(tick);
      else prev.current = target;
    };
    requestAnimationFrame(tick);
  }, [target, duration]);
  return display;
}

function MarketingAgent() {
  const [activeUseCase, setActiveUseCase] = useState(0);
  const [activeArchitecture, setActiveArchitecture] = useState(0);
  const [expandedArchitecture, setExpandedArchitecture] = useState(null);
  const [activePlatformFeature, setActivePlatformFeature] = useState(0);

  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = React.useState(window.innerHeight);
  React.useEffect(() => {
    const handler = () => { setWindowWidth(window.innerWidth); setWindowHeight(window.innerHeight); };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth < 1024;

  // --- ROI State ---
  const calcRef = useRef(null);
  const [roiRate, setRoiRate] = useState(100);
  const [roiProjects, setRoiProjects] = useState(20);
  const [roiTasks, setRoiTasks] = useState({ brief: true, copy: true, design: true, seo: true, audit: true, testing: true });
  const [showCelebration, setShowCelebration] = useState(false);
  const [confettiActive] = useState(false);
  const [roiTouched, setRoiTouched] = useState(false);

  const TASK_DEFS = {
    brief:   { label: 'Brief Generation',      trad: 10, agent: 1,   icon: <RiMagicLine size={24} color="#38bdf8" /> },
    copy:    { label: 'Ad Copywriting',        trad: 16, agent: 2,   icon: <RiMegaphoneLine size={24} color="#a78bfa" /> },
    design:  { label: 'Asset Production',      trad: 24, agent: 3,   icon: <RiBrushLine size={24} color="#34d399" /> },
    seo:     { label: 'SEO Implementation',    trad: 20, agent: 2,   icon: <RiLayoutGridLine size={24} color="#fbbf24" /> },
    audit:   { label: 'AEO / Share-of-Model',  trad: 40, agent: 4,   icon: <RiSearchEyeLine size={24} color="#f87171" /> },
    testing: { label: 'A/B Testing Analysis',  trad: 12, agent: 1,   icon: <RiLineChartLine size={24} color="#38bdf8" /> },
  };

  const savedHrsPerProject = Object.entries(roiTasks).filter(([, v]) => v).reduce((acc, [k]) => acc + TASK_DEFS[k].trad - TASK_DEFS[k].agent, 0);
  const dollarSavedPerYear = Math.round(savedHrsPerProject * roiProjects * roiRate);
  const weeksSavedPerYear = Math.round((savedHrsPerProject * roiProjects) / 40);
  const totalTradHrs = Object.values(TASK_DEFS).reduce((a, t) => a + t.trad, 0);
  const selTradHrs = Object.entries(roiTasks).filter(([,v]) => v).reduce((a, [k]) => a + TASK_DEFS[k].trad, 0);
  const ttmReduction = Math.round((selTradHrs / totalTradHrs) * 100) || 0;

  const animDollar = useCountUp(dollarSavedPerYear);
  const animWeeks  = useCountUp(weeksSavedPerYear);
  const animTTM    = useCountUp(ttmReduction);
  const fmtLive = (n) => n >= 1000000 ? `$${(n/1000000).toFixed(1)}M` : `$${Math.round(n/1000)}K`;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // --- Use Cases ---
  const useCases = [
    {
      id: 'b2b',
      label: 'B2B SaaS Launch',
      cloud: 'Omni-channel',
      cloudBadgeBg: '#eff6ff',
      cloudBadgeBorder: 'rgba(56,189,248,0.25)',
      cloudBadgeText: '#1e40af',
      diagrams: [
        { id: 'b2b-01', src: '/images/aeo-sample/cover.png', title: 'AEO Audit', tag: 'Step 01', desc: 'Identify Share-of-Model against rivals before launching the campaign.' },
        { id: 'b2b-02', src: '/images/aeo-sample/share-of-model.png', title: 'Model Visibility', tag: 'Step 02', desc: 'Measure inclusion rates in ChatGPT and Perplexity.' },
        { id: 'b2b-03', src: '/images/aeo-sample/scorecard.png', title: 'Content Scorecard', tag: 'Step 03', desc: 'Five-dimension rating of existing B2B assets.' },
        { id: 'b2b-04', src: '/images/aeo-sample/trust-signals.png', title: 'Trust Signals', tag: 'Step 04', desc: 'Schema and EEAT validation for the launch.' },
      ]
    },
    {
      id: 'ecommerce',
      label: 'E-commerce Peak Season',
      cloud: 'Performance',
      cloudBadgeBg: '#fff8f0',
      cloudBadgeBorder: 'rgba(255,153,0,0.25)',
      cloudBadgeText: '#92400e',
      diagrams: [
        { id: 'ec-01', src: '/images/agents_hq/visual_gen.png', title: 'Creative Brief', tag: 'Step 01', desc: 'Autonomously generated hooks and angles for BFCM.' },
        { id: 'ec-02', src: '/images/agents_hq/visual_gen.png', title: 'Asset Generation', tag: 'Step 02', desc: 'Static and templated video ad variations.' },
        { id: 'ec-03', src: '/images/agents_hq/visual_gen.png', title: 'A/B Testing', tag: 'Step 03', desc: 'Multi-variant testing setup across platforms.' },
        { id: 'ec-04', src: '/images/agents_hq/visual_gen.png', title: 'Budget Allocation', tag: 'Step 04', desc: 'Dynamic budget shifting based on ROAS.' },
      ]
    }
  ];

  const currentUseCase = useCases[activeUseCase];
  const architectures = currentUseCase.diagrams;

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActiveArchitecture((current) => (current + 1) % architectures.length);
    }, 3200);
    return () => window.clearInterval(intervalId);
  }, [architectures.length, activeUseCase]);

  // --- Platform Features ---
  const platformFeatures = [
    { title: 'Answer Engine Optimization', desc: 'Full audits of your brand visibility inside LLMs.', img: '/images/aeo-sample/share-of-model.png', alt: 'AEO', tag: 'Discovery' },
    { title: 'Omni-Channel Creative', desc: 'Generate briefs, copy, and visuals in seconds.', img: '/images/agents_hq/visual_gen.png', alt: 'Creative', tag: 'Production' },
    { title: 'Performance Intelligence', desc: 'Analyze ad fatigue and dynamically allocate budget.', img: '/images/agents_hq/visual_gen.png', alt: 'Performance', tag: 'Optimization' },
  ];

  // --- Unified Capabilities ---
  const unifiedCapabilities = [
    {
      title: 'Marketing Desk, 24/7',
      desc: 'Bring marketing questions whenever you need them. Use the workspace for live strategy reviews, rapid brief discovery, and ad tradeoff discussions.',
      icon: <RiMagicLine size={22} />,
      tint: '#0ea5e9',
      bg: 'linear-gradient(135deg, rgba(14,165,233,0.18), rgba(37,99,235,0.1))',
    },
    {
      title: 'Share-of-Model Audits',
      desc: 'Measure how often answer engines name and cite your brand versus rivals. Remediation roadmaps are generated instantly.',
      icon: <RiRadarLine size={22} />,
      tint: '#2563eb',
      bg: 'linear-gradient(135deg, rgba(37,99,235,0.16), rgba(99,102,241,0.1))',
    },
    {
      title: 'Creative Engine',
      desc: 'Produce ad copy, static images, and templated videos tailored for any platform from a single unified brief.',
      icon: <RiBrushLine size={22} />,
      tint: '#7c3aed',
      bg: 'linear-gradient(135deg, rgba(124,58,237,0.16), rgba(168,85,247,0.08))',
    },
    {
      title: 'Budget & Fatigue Tracking',
      desc: 'Never waste ad spend. Detect creative fatigue and dynamically manage budgets across channels for peak ROI.',
      icon: <RiLineChartLine size={22} />,
      tint: '#0891b2',
      bg: 'linear-gradient(135deg, rgba(8,145,178,0.16), rgba(6,182,212,0.08))',
    },
    {
      title: 'Brand Compliance',
      desc: 'Automated compliance checks against your brand guidelines before any asset is cleared for publication.',
      icon: <RiShieldStarLine size={22} />,
      tint: '#0f766e',
      bg: 'linear-gradient(135deg, rgba(15,118,110,0.16), rgba(20,184,166,0.08))',
    },
    {
      title: 'Technical SEO',
      desc: 'Optimize schemas, validate programmatic pages, and track keyword rankings automatically.',
      icon: <RiSearchEyeLine size={22} />,
      tint: '#1d4ed8',
      bg: 'linear-gradient(135deg, rgba(29,78,216,0.16), rgba(59,130,246,0.08))',
    },
    {
      title: 'AI Commerce',
      desc: 'Optimize product listings specifically for AI shopping bots and next-gen semantic search engines.',
      icon: <RiLayoutGridLine size={22} />,
      tint: '#0369a1',
      bg: 'linear-gradient(135deg, rgba(3,105,161,0.16), rgba(14,165,233,0.08))',
    },
  ];

  const targetPersonas = [
    { title: 'Chief Marketing Officers', desc: 'Pressure-test brand positioning and monitor overall Share-of-Model against key industry competitors.' },
    { title: 'Growth & Performance Teams', desc: 'Scale ad testing, detect fatigue early, and automate variant generation.' },
    { title: 'SEO & Content Teams', desc: 'Shift from traditional SEO to Answer Engine Optimization without hiring a dedicated agency.' },
  ];

  const workflowSteps = [
    { step: '01', title: 'Bring the campaign', desc: 'Start with a brief, target audience, or raw product specs.', cloud: <RiMegaphoneLine size={20} color="#FF9900" /> },
    { step: '02', title: 'Agent generates assets', desc: 'Produces omni-channel copy, visuals, and schemas instantly.', cloud: <RiMagicLine size={20} color="#4285F4" /> },
    { step: '03', title: 'Launch & Optimize', desc: 'Deploys campaigns and continuously tests for fatigue and ROI.', cloud: <RiLineChartLine size={20} color="#34d399" /> },
  ];

  return (
    <>
      <SEO pathname="/agents/marketing" agentKey="Marketing" />
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 9999 }}>
        <Confetti width={windowWidth} height={windowHeight} recycle={false} numberOfPieces={confettiActive ? 300 : 0} gravity={0.15} />
      </div>

      <main style={{ minHeight: '100vh', background: 'var(--color-bg-light)', color: '#0f172a', fontFamily: '"Inter", -apple-system, sans-serif' }}>
        
        {/* HERO */}
        <section style={{ paddingTop: '120px', paddingBottom: '80px', background: 'linear-gradient(180deg, rgba(248,250,252,1) 0%, #ffffff 100%)', position: 'relative', overflow: 'hidden' }}>
          <div className="container" style={{ position: 'relative', zIndex: 10, maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.1fr 0.9fr', gap: '60px', alignItems: 'center' }}>
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
                <span style={{ display: 'inline-block', padding: '6px 14px', borderRadius: '999px', background: 'rgba(79,70,229,0.1)', color: '#4f46e5', fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '24px' }}>
                  Marketing Agent
                </span>
                <h1 style={{ fontSize: isMobile ? '2.8rem' : '4.2rem', fontWeight: 900, lineHeight: 1.05, letterSpacing: '-0.04em', color: '#0f172a', marginBottom: '24px' }}>
                  The entire marketing agency, <span style={{ color: '#4f46e5' }}>on autopilot</span>.
                </h1>
                <p style={{ fontSize: '1.25rem', color: '#475569', lineHeight: 1.6, marginBottom: '40px', maxWidth: '600px' }}>
                  From Answer Engine Optimization (AEO) and Share-of-Model auditing to autonomous omni-channel creative generation and performance intelligence.
                </p>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <a href="https://app.eagentix.com/register" target="_blank" rel="noopener noreferrer" style={{ background: '#0f172a', color: 'white', padding: '16px 32px', borderRadius: '12px', fontSize: '1.1rem', fontWeight: 700, textDecoration: 'none', boxShadow: '0 10px 25px -5px rgba(15,23,42,0.3)', transition: 'all 0.2s ease' }}>
                    Deploy Marketing Agent
                  </a>
                  <button onClick={() => { document.getElementById('roi-calculator').scrollIntoView({ behavior: 'smooth' }); }} style={{ background: 'white', color: '#0f172a', padding: '16px 32px', borderRadius: '12px', fontSize: '1.1rem', fontWeight: 700, border: '1px solid #cbd5e1', cursor: 'pointer', transition: 'all 0.2s ease' }}>
                    Calculate ROI
                  </button>
                </div>
              </motion.div>
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.2 }}>
                <div style={{ position: 'relative', width: '100%', paddingBottom: '100%' }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)', borderRadius: '40px', transform: 'rotate(-3deg)', boxShadow: '0 25px 50px -12px rgba(79,70,229,0.25)' }} />
                  <div style={{ position: 'absolute', inset: 0, background: '#ffffff', borderRadius: '40px', transform: 'rotate(3deg)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(79,70,229,0.1)' }}>
                    <img src="/images/agents_hq/visual_gen.png" alt="Marketing Agent" style={{ width: '60%', height: '60%', objectFit: 'contain' }} />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ROI CALCULATOR */}
        <section id="roi-calculator" ref={calcRef} style={{ padding: isMobile ? '60px 0' : '100px 0', background: '#0f172a', color: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 style={{ fontSize: isMobile ? '2rem' : '3rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em' }}>Calculate Your Agency Savings</h2>
              <p style={{ fontSize: '1.1rem', color: '#94a3b8', maxWidth: '600px', margin: '16px auto 0' }}>Select the marketing tasks you want the agent to automate, and immediately see the reduction in time-to-market and external agency spend.</p>
            </div>

            {/* TASK TOGGLES */}
            <div style={{ marginBottom: '48px' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '16px' }}>Tasks the Agent Automates</div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(6, 1fr)', gap: '12px' }}>
                {Object.entries(TASK_DEFS).map(([k, t], idx) => {
                  const on = roiTasks[k];
                  const colors = ['#38bdf8', '#a78bfa', '#34d399', '#fbbf24', '#f87171', '#38bdf8'];
                  const color = colors[idx % colors.length];
                  return (
                    <motion.button key={k} onClick={() => { setRoiTasks(prev => ({ ...prev, [k]: !prev[k] })); setRoiTouched(true); }} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                      style={{ padding: '20px 12px', borderRadius: '20px', cursor: 'pointer', border: '2px solid', borderColor: on ? color : 'rgba(255,255,255,0.08)', background: on ? `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)` : 'rgba(255,255,255,0.02)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', minHeight: '120px', justifyContent: 'center' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: on ? `linear-gradient(135deg, ${color}30, ${color}15)` : 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {React.cloneElement(t.icon, { size: 22, color: on ? color : '#64748b' })}
                      </div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: on ? '#ffffff' : '#94a3b8', lineHeight: 1.3, textAlign: 'center' }}>{t.label}</div>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* RESULTS */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '24px' }}>
              <div style={{ background: '#1e293b', borderRadius: '24px', padding: '32px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ color: '#94a3b8', fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Agency Spend Saved</div>
                <div style={{ fontSize: '3.5rem', fontWeight: 900, color: '#34d399', margin: '16px 0' }}>{fmtLive(animDollar)}</div>
                <div style={{ color: '#cbd5e1', fontSize: '0.9rem' }}>per year based on {roiProjects} campaigns</div>
              </div>
              <div style={{ background: '#1e293b', borderRadius: '24px', padding: '32px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ color: '#94a3b8', fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Time-to-Market Reduction</div>
                <div style={{ fontSize: '3.5rem', fontWeight: 900, color: '#60a5fa', margin: '16px 0' }}>{animTTM}%</div>
                <div style={{ color: '#cbd5e1', fontSize: '0.9rem' }}>faster campaign launches</div>
              </div>
              <div style={{ background: '#1e293b', borderRadius: '24px', padding: '32px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ color: '#94a3b8', fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Execution Weeks Saved</div>
                <div style={{ fontSize: '3.5rem', fontWeight: 900, color: '#a78bfa', margin: '16px 0' }}>{animWeeks}</div>
                <div style={{ color: '#cbd5e1', fontSize: '0.9rem' }}>weeks of manual labor eliminated</div>
              </div>
            </div>
          </div>
        </section>

        {/* PLATFORM INTERFACE */}
        <section style={{ padding: isMobile ? '48px 0' : '80px 0', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ textAlign: 'center', margin: '0 auto', maxWidth: '800px' }}>
              <span style={{ color: '#4f46e5', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>Eagentix Platform Interface</span>
              <h2 style={{ fontSize: isMobile ? '1.6rem' : '2.5rem', fontWeight: 800, color: '#0f172a', margin: '16px 0' }}>Conversational Intelligence Meets Execution</h2>
            </div>
            <div style={{ marginTop: '64px', background: '#0f172a', borderRadius: '28px', overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(320px, 0.95fr) minmax(0, 1.25fr)' }}>
                <div style={{ padding: '36px 32px', borderRight: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: '28px' }}>
                  <AnimatePresence mode="wait">
                    <motion.div key={platformFeatures[activePlatformFeature].title} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.28 }}>
                      <span style={{ display: 'inline-flex', padding: '7px 12px', borderRadius: '999px', background: 'rgba(79,70,229,0.2)', color: '#a5b4fc', fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase' }}>{platformFeatures[activePlatformFeature].tag}</span>
                      <h3 style={{ fontSize: '1.7rem', fontWeight: 800, color: 'white', margin: '18px 0 12px' }}>{platformFeatures[activePlatformFeature].title}</h3>
                      <p style={{ color: '#cbd5e1', lineHeight: 1.75 }}>{platformFeatures[activePlatformFeature].desc}</p>
                    </motion.div>
                  </AnimatePresence>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {platformFeatures.map((f, i) => (
                      <motion.button key={f.title} onClick={() => setActivePlatformFeature(i)}
                        style={{ width: i === activePlatformFeature ? '36px' : '10px', height: '10px', borderRadius: '999px', border: 'none', background: i === activePlatformFeature ? '#6366f1' : '#475569', cursor: 'pointer', transition: 'all 0.3s' }} />
                    ))}
                  </div>
                </div>
                <div style={{ overflow: 'hidden', background: '#1e293b' }}>
                  <motion.div animate={{ x: `-${activePlatformFeature * 100}%` }} transition={{ duration: 0.8, ease: 'easeInOut' }} style={{ display: 'flex', width: '300%' }}>
                    {platformFeatures.map(f => (
                      <div key={f.title} style={{ width: '33.33%', flexShrink: 0 }}>
                        <img src={f.img} alt={f.alt} style={{ width: '100%', height: '100%', minHeight: '460px', objectFit: 'cover' }} />
                      </div>
                    ))}
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* AGENT CAPABILITIES */}
        <section style={{ padding: isMobile ? '48px 0' : '100px 0', background: 'radial-gradient(circle at top, #e0e7ff 0%, #f8fafc 42%, #ffffff 100%)' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <span style={{ color: '#4f46e5', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>Full Stack Capabilities</span>
              <h2 style={{ fontSize: isMobile ? '2rem' : '3.5rem', fontWeight: 900, color: '#0f172a', margin: '16px 0' }}>Go to the agent for <span style={{ color: '#4f46e5' }}>real marketing</span> execution</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: '22px' }}>
              {unifiedCapabilities.map((item, index) => (
                <div key={item.title} style={{ background: index === 0 ? 'linear-gradient(145deg, #111827 0%, #1e3a8a 100%)' : 'white', border: '1px solid rgba(148,163,184,0.14)', borderRadius: '24px', padding: '32px', boxShadow: 'rgba(15,23,42,0.08) 0 24px 40px -30px', gridColumn: index === 0 ? '1 / -1' : 'auto' }}>
                  <div style={{ width: '52px', height: '52px', borderRadius: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: item.tint, background: item.bg, marginBottom: '20px' }}>{item.icon}</div>
                  <h3 style={{ fontSize: index === 0 ? '1.8rem' : '1.25rem', fontWeight: 800, color: index === 0 ? 'white' : '#0f172a', margin: '0 0 12px' }}>{item.title}</h3>
                  <p style={{ fontSize: '1.05rem', color: index === 0 ? '#dbeafe' : '#475569', lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* BUYERS + WORKFLOW */}
        <section style={{ padding: isMobile ? '48px 0' : '86px 0', background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(320px, 0.92fr) minmax(0, 1.15fr)', gap: '48px' }}>
              <div>
                <span style={{ color: '#4f46e5', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>Who It Is For</span>
                <h2 style={{ fontSize: isMobile ? '1.6rem' : '2.75rem', fontWeight: 800, color: '#0f172a', margin: '16px 0 18px', lineHeight: 1.1 }}>Built for teams tired of <span style={{ color: '#4f46e5' }}>expensive</span> agencies</h2>
                <div style={{ marginTop: '26px', display: 'grid', gap: '16px' }}>
                  {targetPersonas.map((p) => (
                    <div key={p.title} style={{ padding: '24px', borderRadius: '22px', background: 'white', border: '1px solid rgba(148,163,184,0.16)', boxShadow: 'rgba(15,23,42,0.06) 0 16px 32px -24px' }}>
                      <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: '#0f172a' }}>{p.title}</h3>
                      <p style={{ margin: '10px 0 0', color: '#475569', lineHeight: 1.7 }}>{p.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ borderRadius: '30px', overflow: 'hidden', background: 'linear-gradient(145deg, #0f172a 0%, #312e81 100%)', color: 'white', padding: '40px' }}>
                <span style={{ display: 'inline-flex', padding: '7px 12px', borderRadius: '999px', background: 'rgba(99,102,241,0.2)', color: '#a5b4fc', fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase' }}>How It Works</span>
                <h3 style={{ fontSize: '2rem', fontWeight: 800, margin: '18px 0 24px' }}>From brief to production</h3>
                <div style={{ display: 'grid', gap: '20px' }}>
                  {workflowSteps.map((step) => (
                    <div key={step.step} style={{ display: 'flex', gap: '20px', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '20px' }}>
                      <div style={{ width: '64px', height: '64px', borderRadius: '18px', background: 'rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: '#818cf8', fontWeight: 800, fontSize: '0.72rem' }}>{step.step}</span>
                        {step.cloud}
                      </div>
                      <div>
                        <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: 'white' }}>{step.title}</h4>
                        <p style={{ margin: '8px 0 0', color: '#cbd5e1', fontSize: '0.95rem' }}>{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <FAQSection agentKey="Marketing" />
      </main>
    </>
  );
}

export default MarketingAgent;
